import { useEffect, useRef, useState } from "react";
import type { Position } from "../lib/geo";
import { geocode, formatDistance, type GeocodeResult } from "../lib/nav";
import { haversine } from "../lib/snap";

type Props = {
  position: Position | null;
  onPick: (dest: GeocodeResult) => void;
  onClose: () => void;
};

export function SearchPanel({ position, onPick, onClose }: Props) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Position is read off a ref at fetch time so the live 60 fps GPS stream
  // doesn't re-trigger the debounce / abort the pending search.
  const posRef = useRef(position);
  posRef.current = position;

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    const ctrl = new AbortController();
    const id = window.setTimeout(async () => {
      setLoading(true);
      try {
        const rs = await geocode(q.trim(), posRef.current, ctrl.signal);
        if (!ctrl.signal.aborted) setResults(rs);
      } catch {}
      if (!ctrl.signal.aborted) setLoading(false);
    }, 280); // debounce Nominatim
    return () => { ctrl.abort(); clearTimeout(id); };
  }, [q]);

  return (
    <div className="search-panel">
      <div className="search-head">
        <div className="search-box">
          <SearchIcon />
          <input
            ref={inputRef}
            // type="text" not "search" — the latter adds a native clear
            // glyph on top of our custom one.
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hvor skal vi hen?"
            spellCheck={false}
            autoComplete="off"
          />
          {q && <button className="search-clear" onClick={() => setQ("")} aria-label="Ryd">×</button>}
        </div>
        <button className="close-btn" onClick={onClose}>Afbryd</button>
      </div>
      <div className="search-results">
        {loading && <div className="search-loading">Søger…</div>}
        {!loading && q.trim().length >= 2 && results.length === 0 && (
          <div className="search-loading">Ingen resultater</div>
        )}
        {results.map((r, i) => {
          const distanceM = position ? haversine(position.lat, position.lon, r.lat, r.lon) : null;
          const refine = (e: React.MouseEvent) => {
            e.stopPropagation();
            // Use the result's short name as a prefix for further refinement.
            // Trailing space so the caret lands after the name, ready for
            // a house number / extra detail.
            setQ(r.name + " ");
            inputRef.current?.focus();
            // Move caret to the end after React commits the new value.
            requestAnimationFrame(() => {
              const el = inputRef.current;
              if (el) el.setSelectionRange(el.value.length, el.value.length);
            });
          };
          return (
            <div key={`${r.lat}-${r.lon}-${i}`} className="search-result-row">
              <button className="search-result" onClick={() => onPick(r)}>
                <PinIcon />
                <div className="search-result-text">
                  <span className="search-result-name">{r.name}</span>
                  <span className="search-result-line">{r.line}</span>
                </div>
                {distanceM != null && (
                  <span className="search-result-dist tabular">{formatDistance(distanceM)}</span>
                )}
              </button>
              <button
                className="search-refine"
                onClick={refine}
                aria-label={`Brug "${r.name}" som søgeord`}
                title="Brug som søgeord"
              >
                <RefineIcon />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7zm0 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
    </svg>
  );
}
function RefineIcon() {
  // Arrow up-left — the Apple Maps convention for "use this as my query".
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 17 L7 7" />
      <path d="M7 14 L7 7 L14 7" />
    </svg>
  );
}
